/**
 * Tenant administration view (Administrator-only).
 *
 * Why this exists (REQUIREMENTS §2.2 — multi-tenant from day one):
 *   The platform serves multiple customer organizations. Each tenant has its
 *   own Entra app registration, its own SSO group, its own custom domain.
 *   Onboarding a new tenant requires:
 *     1. A row here with that config
 *     2. The customer adds DNS records (shown in the DNS-instructions panel)
 *     3. Their Entra admin grants Group.Read.All consent on the app reg
 *     4. ACA hostname binding + cert (separate manual flow — not in this UI)
 *
 * Per Tom's standing rules (REQUIREMENTS §4.15):
 *   - Sortable gridview columns
 *   - Auto-faceted filters on fields with <10 distinct values
 *   - Fluid vmin scaling (uses .pad-fluid utility classes)
 *
 * Per REQUIREMENTS §5.5:
 *   - clientSecret VALUE is never displayed (the API returns only a boolean
 *     `hasEntraClientSecret` flag, never the secret ref name or value).
 *
 * Tom's explicit directive on 2026-05-28:
 *   - This tool MUST NOT add DNS records on its own. It only displays them
 *     for the customer/operator to add manually at their DNS provider.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, Search, Save, X, Globe, Copy, Check } from "lucide-react";
import { api, ApiError } from "../lib/api";

type TenantDomain = {
  id: string;
  hostname: string;
  isPrimary: boolean;
  acaBindingStatus: string | null;
};

type Tenant = {
  id: string;
  slug: string;
  name: string;
  status: string;
  entraTenantId: string | null;
  entraApplicationId: string | null;
  entraGroupOid: string | null;
  hasEntraClientSecret: boolean;
  monthlyTwilioBudgetUsd: string; // Prisma Decimal serializes as string
  // Tenant-default outbound caller-id (E.164). Used in outbound TwiML + transfer
  // when the placing user has no directDialNumber. null = fall back to env default.
  outboundCallerId: string | null;
  domains: TenantDomain[];
  userCount?: number;
  createdAt: string;
  updatedAt: string;
};

type DnsRecord = {
  type: "CNAME" | "TXT";
  name: string;
  value: string;
  ttl: number;
  note: string;
};

type DnsInstructions = {
  customDomain: string | null;
  bindingStatus?: string | null;
  instructions: DnsRecord[];
  message?: string;
};

const FACET_FIELDS: Array<{ key: keyof Tenant; label: string }> = [
  { key: "status", label: "Status" },
];

export function TenantsView() {
  const [items, setItems] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [facetFilters, setFacetFilters] = useState<Record<string, Set<string>>>({});
  const [sortKey, setSortKey] = useState<keyof Tenant>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [showAdd, setShowAdd] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dnsByTenant, setDnsByTenant] = useState<Record<string, DnsInstructions>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api<{ items: Tenant[] }>("/api/admin/tenants");
      setItems(res.items);
      setError(null);
    } catch (e) {
      const msg = e instanceof ApiError
        ? `${e.status}: ${(e.body as { error?: string; message?: string })?.message ?? (e.body as { error?: string })?.error ?? e.message}`
        : (e as Error).message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const loadDns = useCallback(async (tenantId: string) => {
    try {
      const res = await api<DnsInstructions>(`/api/admin/tenants/${tenantId}/dns-instructions`);
      setDnsByTenant((prev) => ({ ...prev, [tenantId]: res }));
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  // Auto-facets per REQUIREMENTS §4.15: only render facet UI when distinct
  // values < 10. With only `status` declared facetable, this is straightforward.
  const facets = useMemo(() => {
    const out: Record<string, Array<{ value: string; count: number }>> = {};
    for (const f of FACET_FIELDS) {
      const counts = new Map<string, number>();
      for (const t of items) {
        counts.set(String(t[f.key] ?? "—"), (counts.get(String(t[f.key] ?? "—")) ?? 0) + 1);
      }
      if (counts.size > 0 && counts.size < 10) {
        out[f.key] = [...counts.entries()]
          .map(([value, count]) => ({ value, count }))
          .sort((a, b) => (b.count - a.count) || a.value.localeCompare(b.value));
      }
    }
    return out;
  }, [items]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((t) => {
      if (term) {
        const hay = [t.name, t.slug, ...(t.domains?.map((d) => d.hostname) ?? [])]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      for (const [k, set] of Object.entries(facetFilters)) {
        if (set.size === 0) continue;
        const v = String((t as Record<string, unknown>)[k] ?? "—");
        if (!set.has(v)) return false;
      }
      return true;
    });
  }, [items, search, facetFilters]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (k: keyof Tenant) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };

  const toggleFacet = (key: string, value: string) => {
    setFacetFilters((prev) => {
      const next = { ...prev };
      const set = new Set(next[key] ?? []);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      next[key] = set;
      return next;
    });
  };

  const expand = (id: string) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    // Always re-fetch on expand so ACA binding status / DNS records stay
    // current. The previous "fetch once, cache forever" pattern showed
    // stale data after a binding completed mid-session.
    void loadDns(id);
  };

  return (
    <div className="pad-fluid">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-rogue-900">Tenants</h1>
          <div className="text-xs text-rogue-500 mt-1">
            Platform-admin view. Adding a tenant doesn't touch DNS — you'll get the records
            to add at the customer's DNS provider.
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAdd(true)}
            className="bg-rogue-700 hover:bg-rogue-800 text-white rounded-lg px-3 py-1.5 text-sm flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Add tenant
          </button>
          <button
            onClick={() => void load()}
            className="text-sm text-rogue-600 hover:text-rogue-800 flex items-center gap-1"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 rounded-lg pad-fluid mb-4 text-sm">{error}</div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl shadow pad-fluid mb-4 flex items-center gap-2">
        <Search className="w-4 h-4 text-rogue-500" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, slug, or domain"
          className="flex-1 outline-none text-sm"
        />
      </div>

      {/* Facets */}
      {Object.keys(facets).length > 0 && (
        <div className="bg-white rounded-xl shadow pad-fluid mb-4 flex flex-wrap gap-fluid">
          {FACET_FIELDS.filter((f) => facets[f.key as string]).map((f) => {
            const values = facets[f.key as string]!;
            const selected = facetFilters[f.key as string] ?? new Set();
            return (
              <div key={f.key as string} className="flex flex-col gap-1 min-w-[8rem]">
                <div className="text-xs uppercase tracking-wide text-rogue-500">{f.label}</div>
                <div className="flex flex-wrap gap-1">
                  {values.map((v) => {
                    const on = selected.has(v.value);
                    return (
                      <button
                        key={v.value}
                        onClick={() => toggleFacet(f.key as string, v.value)}
                        className={[
                          "text-xs px-2 py-1 rounded-full border transition-colors",
                          on ? "bg-rogue-700 text-white border-rogue-700" : "bg-rogue-50 text-rogue-700 border-rogue-200 hover:bg-rogue-100",
                        ].join(" ")}
                      >
                        {v.value} <span className="opacity-60">{v.count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Gridview */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-rogue-50">
            <tr className="text-left text-xs uppercase tracking-wide text-rogue-600">
              <Th onClick={() => toggleSort("name")} active={sortKey === "name"} dir={sortDir}>Tenant</Th>
              <Th onClick={() => toggleSort("slug")} active={sortKey === "slug"} dir={sortDir}>Slug</Th>
              <Th onClick={() => toggleSort("status")} active={sortKey === "status"} dir={sortDir}>Status</Th>
              <th className="pad-fluid">Primary domain</th>
              <th className="pad-fluid">Entra config</th>
              <th className="pad-fluid">Users</th>
              <th className="pad-fluid"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rogue-100">
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={7} className="pad-fluid text-center text-rogue-500">No tenants match.</td></tr>
            )}
            {sorted.map((t) => {
              const primary = t.domains?.find((d) => d.isPrimary);
              const dns = dnsByTenant[t.id];
              const open = expandedId === t.id;
              // React.Fragment with an explicit `key` so the reconciler
              // tracks the row pair correctly during filter/sort changes.
              // Bare `<>...</>` fragments do NOT accept a key.
              return (
                <Fragment key={t.id}>
                  <tr className={open ? "bg-rogue-50" : ""}>
                    <td className="pad-fluid font-medium text-rogue-900">{t.name}</td>
                    <td className="pad-fluid font-mono text-rogue-700">
                      <div>{t.slug}</div>
                      {/* Outbound caller-id default — dim placeholder when null
                          so admins can see at a glance which tenants still
                          rely on the env-level fallback. */}
                      <div className="text-[10px] mt-0.5">
                        {t.outboundCallerId ? (
                          <span className="text-rogue-500">{t.outboundCallerId}</span>
                        ) : (
                          <span className="text-rogue-300 italic">env-default</span>
                        )}
                      </div>
                    </td>
                    <td className="pad-fluid">
                      <span className={[
                        "inline-flex text-xs px-2 py-1 rounded-full",
                        t.status === "active" ? "bg-emerald-100 text-emerald-800" :
                        t.status === "suspended" ? "bg-amber-100 text-amber-800" :
                        "bg-rogue-100 text-rogue-700",
                      ].join(" ")}>{t.status}</span>
                    </td>
                    <td className="pad-fluid text-rogue-700">
                      {primary?.hostname ?? <span className="text-rogue-400">—</span>}
                      {primary && (
                        <span className={[
                          "ml-2 text-xs px-2 py-0.5 rounded-full",
                          primary.acaBindingStatus === "active" ? "bg-emerald-50 text-emerald-700" :
                          primary.acaBindingStatus === "pending" ? "bg-amber-50 text-amber-700" :
                          "bg-rogue-50 text-rogue-600",
                        ].join(" ")}>{primary.acaBindingStatus ?? "—"}</span>
                      )}
                    </td>
                    <td className="pad-fluid">
                      <div className="flex gap-1 text-xs">
                        {t.entraApplicationId && <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">App</span>}
                        {t.entraGroupOid && <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">Group</span>}
                        {t.hasEntraClientSecret && <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Secret</span>}
                      </div>
                    </td>
                    <td className="pad-fluid text-rogue-700">{t.userCount ?? "—"}</td>
                    <td className="pad-fluid text-right">
                      <button
                        onClick={() => expand(t.id)}
                        className="text-xs text-rogue-600 hover:text-rogue-900 flex items-center gap-1 ml-auto"
                      >
                        <Globe className="w-3 h-3" /> {open ? "Hide" : "DNS"}
                      </button>
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-rogue-50">
                      <td colSpan={7} className="pad-fluid">
                        <DnsPanel dns={dns} hostname={primary?.hostname ?? null} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddTenantModal
          onClose={() => setShowAdd(false)}
          onCreated={(t) => {
            setShowAdd(false);
            void load();
            const primary = t.domains?.find((d) => d.isPrimary);
            if (primary) {
              setExpandedId(t.id);
              void loadDns(t.id);
            }
          }}
        />
      )}
    </div>
  );
}

function Th({ children, onClick, active, dir }: {
  children: React.ReactNode;
  onClick: () => void;
  active: boolean;
  dir: "asc" | "desc";
}): JSX.Element {
  return (
    <th className="pad-fluid">
      <button onClick={onClick} className="flex items-center gap-1 hover:text-rogue-900">
        <span>{children}</span>
        {active && <span className="text-rogue-700">{dir === "asc" ? "▲" : "▼"}</span>}
      </button>
    </th>
  );
}

function DnsPanel({ dns, hostname }: { dns: DnsInstructions | undefined; hostname: string | null }) {
  if (!hostname) {
    return (
      <div className="text-sm text-rogue-500 italic">
        No primary custom domain configured.
      </div>
    );
  }
  if (!dns) return <div className="text-sm text-rogue-500">Loading DNS instructions…</div>;
  if (!dns.instructions || dns.instructions.length === 0) {
    return (
      <div className="text-sm text-rogue-500">{dns.message || "No DNS instructions available."}</div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="text-sm text-rogue-900 font-medium">
        Add these records at your DNS provider for <span className="font-mono">{dns.customDomain}</span>:
      </div>
      <div className="grid gap-2">
        {dns.instructions.map((r, i) => (
          <CopyableRecord key={i} record={r} />
        ))}
      </div>
      <div className="text-xs text-rogue-500 italic">
        We do not add DNS records on your behalf. Once you've added both records, ping ops to bind the
        hostname and provision the managed certificate in ACA.
      </div>
    </div>
  );
}

function CopyableRecord({ record }: { record: DnsRecord }) {
  const [copied, setCopied] = useState<"name" | "value" | null>(null);
  const copy = (s: string, what: "name" | "value") => {
    void navigator.clipboard.writeText(s);
    setCopied(what);
    setTimeout(() => setCopied(null), 1500);
  };
  return (
    <div className="bg-white rounded-lg p-3 grid grid-cols-[auto_1fr_1fr_auto] gap-3 items-center text-sm">
      <span className="font-mono text-xs px-2 py-1 rounded bg-rogue-100 text-rogue-800">{record.type}</span>
      <div className="flex flex-col">
        <span className="text-xs text-rogue-500">Name</span>
        <button
          className="font-mono text-rogue-900 text-left hover:text-rogue-700"
          onClick={() => copy(record.name, "name")}
          title="Copy"
        >
          {record.name} {copied === "name" ? <Check className="inline w-3 h-3 text-emerald-600" /> : <Copy className="inline w-3 h-3 opacity-40" />}
        </button>
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-xs text-rogue-500">Value</span>
        <button
          className="font-mono text-rogue-900 text-left hover:text-rogue-700 truncate"
          onClick={() => copy(record.value, "value")}
          title={record.value}
        >
          {record.value} {copied === "value" ? <Check className="inline w-3 h-3 text-emerald-600" /> : <Copy className="inline w-3 h-3 opacity-40" />}
        </button>
      </div>
      <div className="text-xs text-rogue-500 max-w-[16rem]">{record.note}</div>
    </div>
  );
}

function AddTenantModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (t: Tenant) => void;
}) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [customDomain, setCustomDomain] = useState("");
  const [outboundCallerId, setOutboundCallerId] = useState("");
  const [entraTenantId, setEntraTenantId] = useState("");
  const [entraApplicationId, setEntraApplicationId] = useState("");
  const [entraGroupOid, setEntraGroupOid] = useState("");
  const [entraClientSecret, setEntraClientSecret] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await api<{ item: Tenant }>("/api/admin/tenants", {
        method: "POST",
        body: JSON.stringify({
          slug, name, customDomain: customDomain || undefined,
          outboundCallerId: outboundCallerId || undefined,
          entraTenantId: entraTenantId || undefined,
          entraApplicationId: entraApplicationId || undefined,
          entraGroupOid: entraGroupOid || undefined,
          entraClientSecret: entraClientSecret || undefined,
        }),
      });
      onCreated(res.item);
    } catch (e) {
      const msg = e instanceof ApiError
        ? `${e.status}: ${(e.body as { error?: string; message?: string })?.message ?? (e.body as { error?: string })?.error ?? e.message}`
        : (e as Error).message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center pad-fluid" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-[min(90vw,40rem)] max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between pad-fluid border-b border-rogue-200">
          <h2 className="text-xl font-bold text-rogue-900">Add tenant</h2>
          <button onClick={onClose} className="text-rogue-500 hover:text-rogue-900 p-2"><X className="w-5 h-5" /></button>
        </div>
        <div className="pad-fluid grid gap-3">
          {error && <div className="bg-red-50 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
          <Field label="Slug" hint="3-63 chars, lowercase letters/digits/hyphens (no edges)">
            <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="acme" className="w-full border border-rogue-200 rounded px-3 py-2 text-sm font-mono" />
          </Field>
          <Field label="Name" hint="Display name for the tenant">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Corp" className="w-full border border-rogue-200 rounded px-3 py-2 text-sm" />
          </Field>
          <Field label="Custom domain" hint="Their FQDN — they CNAME to our ACA frontend">
            <input value={customDomain} onChange={(e) => setCustomDomain(e.target.value)} placeholder="rogue.acme.com" className="w-full border border-rogue-200 rounded px-3 py-2 text-sm font-mono" />
          </Field>
          <Field label="Outbound caller ID" hint="E.164 default for outbound calls when a placing user has no directDialNumber. Optional — falls back to the platform env default.">
            <input value={outboundCallerId} onChange={(e) => setOutboundCallerId(e.target.value)} placeholder="+19185551234" className="w-full border border-rogue-200 rounded px-3 py-2 text-sm font-mono" />
          </Field>
          <div className="border-t border-rogue-200 pt-3 mt-1">
            <div className="text-sm font-medium text-rogue-900 mb-2">Entra (Microsoft 365) SSO config</div>
            <div className="text-xs text-rogue-500 mb-3">
              Customer creates an Entra app registration in their tenant. They grant <span className="font-mono">Group.Read.All</span>
              {" "}admin consent. They paste the values below.
            </div>
            <Field label="Entra tenant ID" hint="The customer's Entra directory ID">
              <input value={entraTenantId} onChange={(e) => setEntraTenantId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="w-full border border-rogue-200 rounded px-3 py-2 text-sm font-mono" />
            </Field>
            <Field label="Entra application (client) ID" hint="The app registration's clientId">
              <input value={entraApplicationId} onChange={(e) => setEntraApplicationId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="w-full border border-rogue-200 rounded px-3 py-2 text-sm font-mono mt-3" />
            </Field>
            <Field label="Entra group OID" hint="Security group whose members get access">
              <input value={entraGroupOid} onChange={(e) => setEntraGroupOid(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="w-full border border-rogue-200 rounded px-3 py-2 text-sm font-mono mt-3" />
            </Field>
            <Field label="Client secret" hint="VALUE stored in Key Vault, not in PG. Submit once on creation; operator copies it into KV under the generated reference.">
              <input type="password" value={entraClientSecret} onChange={(e) => setEntraClientSecret(e.target.value)} placeholder="••••••••" className="w-full border border-rogue-200 rounded px-3 py-2 text-sm font-mono mt-3" />
            </Field>
          </div>
        </div>
        <div className="pad-fluid border-t border-rogue-200 flex justify-end gap-2">
          <button onClick={onClose} className="bg-rogue-100 hover:bg-rogue-200 text-rogue-800 rounded-lg px-3 py-1.5 text-sm">Cancel</button>
          <button
            onClick={() => void submit()}
            disabled={submitting || !slug || !name}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white rounded-lg px-3 py-1.5 text-sm flex items-center gap-1"
          >
            <Save className="w-4 h-4" /> Create
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-rogue-500 mb-1">{label}</label>
      {children}
      {hint && <div className="text-xs text-rogue-500 mt-1">{hint}</div>}
    </div>
  );
}
